import { useEffect, useRef, useCallback } from 'react';
import { Map as OLMap } from 'ol';
import { Vector as VectorSource } from 'ol/source';
import { Vector as VectorLayer } from 'ol/layer';
import { Draw, Snap, Modify } from 'ol/interaction';
import { Style, Stroke, Circle, Fill, Text } from 'ol/style';
import GeoJSON from 'ol/format/GeoJSON';
import { NO_POPUP_TOOLS, useMapStore } from '@/stores/mapStore';
import { LayerDefinition, SystemLayerKey } from '@packages/database/types';

interface Props {
  map: OLMap | null;
}

const buildStyle = (
  layerDef: LayerDefinition | null,
  assetType?: string,
  properties?: Record<string, any>,
) => {
  // ── Default generic tools — simple neutral styles ─────────────────────────
  if (!layerDef) {
    if (assetType === 'POLYGON') {
      return new Style({
        fill: new Fill({ color: 'rgba(206, 168, 168, 0.2)' }),
        stroke: new Stroke({ color: 'red', width: 2 }),
      });
    }
    if (assetType === 'LABEL') {
      const labelText = properties?.label;
      return new Style({
        // Small invisible anchor so the point is still selectable
        image: new Circle({
          radius: 3,
          fill: new Fill({ color: 'rgba(0,0,0,0)' }),
          stroke: new Stroke({ color: 'rgba(0,0,0,0)', width: 0 }),
        }),
        text: labelText
          ? new Text({
              text: labelText,
              font: 'bold 13px "Inter", sans-serif',
              fill: new Fill({ color: '#1e293b' }),
              stroke: new Stroke({ color: '#ffffff', width: 3 }),
              backgroundFill: new Fill({ color: 'rgba(255,255,255,0.85)' }),
              padding: [3, 6, 3, 6],
              offsetY: -16,
            })
          : undefined,
      });
    }
    if (assetType === 'LINESTRING' || assetType === 'FREEHAND') {
      return new Style({
        stroke: new Stroke({ color: 'red', width: 2 }),
      });
    }
    // POINT / LABEL / fallback
    return new Style({
      image: new Circle({
        radius: 5,
        fill: new Fill({ color: 'red' }),
        stroke: new Stroke({ color: 'red', width: 2 }),
      }),
    });
  }

  // ── Layer slug tools — use layer styles ───────────────────────────────────
  const s = layerDef.styles;
  const label = layerDef.displayName ?? '';
  const color = 'red';
  const geomType = layerDef.geomType?.toUpperCase() ?? 'POINT';

  if (geomType === 'LINESTRING' || geomType === 'MULTILINESTRING') {
    return new Style({
      stroke: new Stroke({
        color,
        width: s?.lineWidth ?? 3,
        lineDash: s?.lineDash,
      }),
      text: new Text({
        text: label,
        font: '12px "Inter", sans-serif',
        placement: 'line',
        textBaseline: 'bottom',
        fill: new Fill({ color }),
        stroke: new Stroke({ color: '#ffffff', width: 3 }),
        offsetY: -5,
      }),
    });
  }

  if (geomType === 'POLYGON' || geomType === 'MULTIPOLYGON') {
    return new Style({
      // need fill for opacity
      fill: new Fill({ color: 'rgba(206, 168, 168, 0.2)' }),
      stroke: new Stroke({
        color: color,
        width: s?.lineWidth ?? 2,
      }),
    });
  }

  return new Style({
    image: new Circle({
      radius: s?.pointSize ?? 6,
      fill: new Fill({ color }),
      stroke: new Stroke({ color: color, width: 2 }),
    }),
    text: new Text({
      text: label,
      offsetY: -18,
      font: '600 11px "Inter", sans-serif',
      fill: new Fill({ color: '#1e293b' }),
      backgroundFill: new Fill({ color: 'rgba(255,255,255,0.85)' }),
      padding: [2, 4, 1, 4],
      overflow: true,
    }),
  });
};

export const RedLineSketchLayer = ({ map }: Props) => {
  const sourceRef = useRef<VectorSource | null>(null);

  const {
    interactionMode,
    activeRedlineTool,
    updateRedlineData,
    redlineData,
    setActiveSketchFeatureId,
    layers,
    activeSketchFeatureId,
  } = useMapStore();

  const activeRedlineToolRef = useRef(activeRedlineTool);
  const redlineDataRef = useRef(redlineData);
  const interactionModeRef = useRef(interactionMode);
  const layersRef = useRef(layers);
  const setSketchFeatureIdRef = useRef(setActiveSketchFeatureId);
  const updateRedlineDataRef = useRef(updateRedlineData);

  useEffect(() => {
    activeRedlineToolRef.current = activeRedlineTool;
  }, [activeRedlineTool]);
  useEffect(() => {
    redlineDataRef.current = redlineData;
  }, [redlineData]);
  useEffect(() => {
    interactionModeRef.current = interactionMode;
  }, [interactionMode]);
  useEffect(() => {
    layersRef.current = layers;
  }, [layers]);
  useEffect(() => {
    setSketchFeatureIdRef.current = setActiveSketchFeatureId;
  }, [setActiveSketchFeatureId]);
  useEffect(() => {
    updateRedlineDataRef.current = updateRedlineData;
  }, [updateRedlineData]);

  // ── Find connected asset at coordinate ────────────────────────────────────
  const findConnectedAssetId = useCallback(
    (coordinate: number[]) => {
      if (!map || !sourceRef.current) return null;
      const pixel = map.getPixelFromCoordinate(coordinate);

      const existing = map.getFeaturesAtPixel(pixel, {
        hitTolerance: 10,
        layerFilter: (l) => {
          const lid = l.get('config')?.key;
          return (
            lid &&
            (lid === SystemLayerKey.ASSET || lid === SystemLayerKey.PREMISE)
          );
        },
      });
      if (existing.length > 0) return existing[0].get('id');

      const redlineFeatures =
        sourceRef.current.getFeaturesAtCoordinate(coordinate);
      if (redlineFeatures.length > 0) return redlineFeatures[0].getId();
      return null;
    },
    [map],
  );

  const handleDrawEnd = useCallback(
    (event: any) => {
      if (!sourceRef.current) return;

      const rawFeature = event.feature;
      const geometry = rawFeature.getGeometry();
      const tool = activeRedlineToolRef.current;
      const currentData = redlineDataRef.current;
      const isUpdateMode = interactionModeRef.current === 'addFieldCorrection';
      const writer = new GeoJSON();

      if (tool === 'LABEL') {
        const coord = geometry.getCoordinates();
        const tempId = `label-pending-${Date.now()}`;
        rawFeature.setId(tempId);

        const geoJson = {
          type: 'Feature',
          id: tempId,
          geometry: writer.writeGeometryObject(geometry, {
            dataProjection: 'EPSG:4326',
            featureProjection: 'EPSG:3857',
          }),
          properties: {
            id: tempId,
            assetType: 'LABEL',
            label: '',
            isPending: true,
            created_at: new Date().toISOString(),
          },
        };

        const currentFeatures = currentData?.features ?? [];
        updateRedlineDataRef.current({
          type: 'FeatureCollection',
          features: [...currentFeatures, geoJson],
        } as any);

        sourceRef.current.removeFeature(rawFeature);
        setTimeout(() => setSketchFeatureIdRef.current(tempId), 50);
        return;
      }

      let finalId = `temp-${Date.now()}`;
      let existingProps = {};

      if (isUpdateMode && currentData?.features?.length) {
        const existing: any = currentData.features[0];
        if (existing.id) finalId = existing.id;
        if (existing.properties) existingProps = { ...existing.properties };
      }

      let connections = {
        upstream: [] as string[],
        downstream: [] as string[],
      };
      if (geometry.getType() === 'LineString') {
        const coords = geometry.getCoordinates();
        const start = findConnectedAssetId(coords[0]);
        const end = findConnectedAssetId(coords[coords.length - 1]);
        if (start) connections.upstream = [start];
        if (end) connections.downstream = [end];
      }

      const newFeatureGeoJson = {
        type: 'Feature',
        id: finalId,
        geometry: writer.writeGeometryObject(geometry, {
          dataProjection: 'EPSG:4326',
          featureProjection: 'EPSG:3857',
        }),
        properties: {
          ...existingProps,
          id: finalId,
          assetType: tool,
          created_at: new Date().toISOString(),
          connections,
        },
      };

      const currentFeatures = currentData?.features ?? [];
      updateRedlineDataRef.current({
        type: 'FeatureCollection',
        features: isUpdateMode
          ? [newFeatureGeoJson]
          : [...currentFeatures, newFeatureGeoJson],
      } as any);

      sourceRef.current.removeFeature(rawFeature);

      if (!NO_POPUP_TOOLS.has(tool)) {
        setTimeout(() => setSketchFeatureIdRef.current(finalId), 50);
      }
    },
    [findConnectedAssetId],
  );

  useEffect(() => {
    if (!map) return;

    const source = new VectorSource({ format: new GeoJSON() });
    sourceRef.current = source;

    const layer = new VectorLayer({
      source,
      properties: { type: 'redline' },
      zIndex: 9999,
      style: (f) => {
        const assetType = f.get('assetType') as string;
        const properties = f.getProperties() as Record<string, any>;
        const layerDef =
          layersRef.current.find(
            (l) =>
              l.sourceTable === 'assets' &&
              (l.sourceFilter as any)?.assetType === assetType,
          ) ?? null;
        return buildStyle(layerDef, assetType, properties);
      },
    });

    map.addLayer(layer);
    return () => {
      map.removeLayer(layer);
    };
  }, [map]);

  // ── Data sync: store → OL source ─────────────────────────────────────────
  useEffect(() => {
    const source = sourceRef.current;
    if (!source) return;

    if (!redlineData?.features?.length) {
      source.clear();
      return;
    }

    const format = new GeoJSON();
    const storeIds = new Set(
      redlineData.features
        .filter((f: any) => f.geometry !== null)
        .map((f: any) => f.id),
    );

    // Remove features no longer in store
    source.getFeatures().forEach((f) => {
      if (!storeIds.has(f.getId())) source.removeFeature(f);
    });

    // Add/update — never wipe the whole source
    redlineData.features.forEach((geoJsonF: any) => {
      const id = geoJsonF.id;
      if (!id || !geoJsonF.geometry) return;

      const existing = source.getFeatureById(id);
      if (existing) {
        existing.setProperties(geoJsonF.properties);
      } else {
        const newFeature: any = format.readFeature(geoJsonF, {
          featureProjection: 'EPSG:3857',
        });
        newFeature.setId(id);
        source.addFeature(newFeature);
      }
    });
  }, [redlineData]);

  // ── Interaction management ────────────────────────────────────────────────
  useEffect(() => {
    const isEditing =
      interactionMode === 'redlining' ||
      interactionMode === 'addFieldCorrection';

    const removeDrawInteractions = () => {
      map
        ?.getInteractions()
        .getArray()
        .slice()
        .forEach((i) => {
          if (i instanceof Draw || i instanceof Snap || i instanceof Modify)
            map.removeInteraction(i);
        });
    };

    if (!map || !isEditing || !sourceRef.current) {
      removeDrawInteractions();
      return;
    }

    removeDrawInteractions();

    // Modify — always active while editing
    const modify = new Modify({ source: sourceRef.current });
    modify.on('modifyend', () => {
      const writer = new GeoJSON();
      updateRedlineData(
        writer.writeFeaturesObject(sourceRef.current!.getFeatures(), {
          dataProjection: 'EPSG:4326',
          featureProjection: 'EPSG:3857',
        }),
      );
    });
    map.addInteraction(modify);

    // Draw — only when a tool is selected
    if (activeRedlineTool !== 'NONE') {
      let geometryType: 'Point' | 'LineString' | 'Polygon' | null = null;
      let isFreehand = false;

      if (activeRedlineTool === 'LABEL') geometryType = 'Point';
      else if (activeRedlineTool === 'POINT') geometryType = 'Point';
      else if (activeRedlineTool === 'LINESTRING') geometryType = 'LineString';
      else if (activeRedlineTool === 'POLYGON') geometryType = 'Polygon';
      else if (activeRedlineTool === 'FREEHAND') {
        geometryType = 'LineString';
        isFreehand = true;
      } else {
        const layerDef = layers.find(
          (l) =>
            l.sourceTable === 'assets' &&
            (l.sourceFilter as any)?.assetType === activeRedlineTool,
        );
        const g = layerDef?.geomType?.toUpperCase() ?? '';
        if (g.includes('POLYGON')) geometryType = 'Polygon';
        else if (g.includes('LINESTRING')) geometryType = 'LineString';
        else geometryType = 'Point';
      }

      if (geometryType) {
        const draw = new Draw({
          source: sourceRef.current,
          type: geometryType,
          freehand: isFreehand,
        });

        draw.on('drawend', handleDrawEnd);
        map.addInteraction(draw);

        if (!isFreehand) {
          map.addInteraction(new Snap({ source: sourceRef.current }));
          layers.forEach((l) => {
            if (!l.isVisible) return;
            const lyr = map
              .getLayers()
              .getArray()
              .find((yl) => yl.get('id') === l.id);
            if (lyr instanceof VectorLayer)
              map.addInteraction(new Snap({ source: lyr.getSource() }));
          });
        }
      }
    }

    return () => {
      removeDrawInteractions();
    };
  }, [
    map,
    interactionMode,
    activeRedlineTool,
    // ✅ handleDrawEnd removed from deps — it's stable via refs
    updateRedlineData,
    layers,
  ]);

  return null;
};
